import React, { useEffect, useRef, useState, useCallback } from 'react';
import Highcharts, { r } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import io from 'socket.io-client';
import moment from 'moment-timezone';
import { useStore } from '../states/store';
import DrawHistory from '../components/DrawHistory';
import UserBetsDetails from '../components/UserBetsDetails';
import { soundLibrary } from '../utils/sfx/sound';

import 'highcharts/modules/boost.js';
import ResultSummaryPanel from './ResultSummaryPanel';
import WinningBetPanel from './WinningBetPanel';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';


const OptionsChartHighcharts = ({ height = 250, className = '', onZoom, onReset }) => {
  const { timerStatus, currentRound, drawOpenPrice, resultsData, isSoundOn, toggleSound, toggleMute } = useStore();
  const chartRef            = useRef(null);
  const chartContainerRef   = useRef(null);
  const socketRef           = useRef(null);
  const cleanupIntervalRef  = useRef(null);
  const rafRef              = useRef(null);
  const latestPriceRef      = useRef(null);
  const ANIMATION_TYPE      = 'linear';

  // Spring physics
  const animationRef = useRef({
    currentY: null, targetY: null, velocity: 0,
    stiffness: 0, damping: 0, sensitivity: 0.0000001,
  });

  const currentRangeRef   = useRef({ min: null, max: null });
  const [initialized, setInitialized]         = useState(false);
  const [isFillFixed, setIsFillFixed]         = useState(false);
  const [fixedPrice, setFixedPrice]           = useState(null);
  const previousPriceRef  = useRef(null);
  const [isZoomed, setIsZoomed]               = useState(false);
  const [isAnimatingZoom, setIsAnimatingZoom] = useState(false);
  const [isChartReloaded, setIsChartReloaded] = useState(false);

  const markerHitStateRef = useRef({
    firstMarker:  { hit: false, time: null },
    secondMarker: { hit: false, time: null },
  });

  // ─── Trim / rebuild refs ───────────────────────────────────────────────────
  const isRebuildingRef     = useRef(false);
  const rebuildScheduledRef = useRef(false);
  const rebuildTimeoutRef   = useRef(null);
  const isFillFixedRef      = useRef(isFillFixed);
  useEffect(() => { isFillFixedRef.current = isFillFixed; }, [isFillFixed]);

  // ─── Marker Cache Helpers ─────────────────────────────────────────────────
  const MARKER_CACHE_KEY = 'options_chart_marker_cache';
  const saveMarkerCache  = (roundId, drawCloseTime) => {
    try { localStorage.setItem(MARKER_CACHE_KEY, JSON.stringify({ roundId, drawCloseTime })); } catch (e) {}
  };
  const loadMarkerCache  = () => {
    try { const r = localStorage.getItem(MARKER_CACHE_KEY); return r ? JSON.parse(r) : null; } catch (e) { return null; }
  };
  const clearMarkerCache = () => {
    try { localStorage.removeItem(MARKER_CACHE_KEY); } catch (e) {}
  };

  const PING_CONFIG = {
    threshold: 0.01, pingDuration: 100, maxSize: 20,
    startOpacity: 0.05, endOpacity: 0, easing: ANIMATION_TYPE, strokeWidth: 2,
  };

  // ─── Constants ────────────────────────────────────────────────────────────
  const VISIBLE_BARS            = 100;
  const MS_PER_BAR              = 100;
  const WINDOW_MS               = VISIBLE_BARS * MS_PER_BAR * 12;
  const FUTURE_BUFFER_MS        = 30000;
  const PAST_BUFFER_MS          = 60 * 60 * 1000;
  const ZOOM_RANGE_MS           = 13 * 1000;
  const ZOOM_ANIMATION_DURATION = 10000;
  const websocketUrl            = process.env.HL_CRYPTO_v1_WS_SERVER;
  const apiUrl                  = process.env.HL_API_v1_SERVER;
  const CHART_ANIMATION         = ANIMATION_TYPE;
  const isBgEnabled             = false;
  const ANIMATION_DURATION      = MS_PER_BAR * 5;
  const chartReloadInterval     = 10 * 60 * 1000;
  const RIGHT_PAD               = WINDOW_MS * 0.5;

  const RETENTION_MULTIPLIER = 5;
  const MIN_POINTS_TO_KEEP   = VISIBLE_BARS * RETENTION_MULTIPLIER;
  const MAX_DATA_POINTS      = Math.max(MIN_POINTS_TO_KEEP, 3000);
  const RETENTION_MS         = WINDOW_MS * RETENTION_MULTIPLIER;

  const KOREAN_TIMEZONE = 'Asia/Seoul';
  const LOCAL_TIMEZONE  = moment.tz.guess();

  const frameCounterRef       = useRef(0);
  const dataPointsCountRef    = useRef(0);
  const performanceMonitorRef = useRef({ lastCleanup: 0 });
  const lastInterpolatedRef   = useRef(0);
  const lastRealPointTsRef    = useRef(0);

  // Micro heartbeat effect configuration
  const HEARTBEAT_CONFIG = {
    interval: ANIMATION_DURATION,  // ms between updates (slower = 150-300ms)
    maxVariation: 0.0003, // 0.03% change
    smoothing: 0.25,      // Moderate smoothness
    enabled: true
  };

  const zoomAnimationRef = useRef({
    startTime: 0,
    startRange:  { min: null, max: null },
    targetRange: { min: null, max: null },
    duration: ZOOM_ANIMATION_DURATION,
    easing: CHART_ANIMATION,
  });

  const addedMarkersRef   = useRef(new Set());
  const currentRoundIdRef = useRef(null);
  const [chartDataLoaded, setChartDataLoaded] = useState(false);
  const chartDataReadyRef = useRef(false);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const safeRedraw = useCallback((chart) => {
    if (chart && chart.redraw && chart.renderer && chart.series && !chart.renderer.forExport) {
      try { chart.redraw(); } catch (e) {}
    }
  }, []);

  const normalizeTime = (t) => {
    const n = Number(t);
    return Number.isNaN(n) ? Date.now() : (n < 1e12 ? n * 1000 : n);
  };

  const parseTimestamp = (timeStr) => {
    if (!timeStr) return NaN;
    try {
      if (typeof timeStr === 'number' && !isNaN(timeStr)) return timeStr < 1e12 ? timeStr * 1000 : timeStr;
      if (typeof timeStr === 'string') {
        const kt = moment.tz(timeStr, 'YYYY-MM-DD HH:mm:ss.SSS', KOREAN_TIMEZONE);
        if (kt.isValid()) return kt.clone().tz(LOCAL_TIMEZONE).valueOf();
        return new Date(timeStr).getTime();
      }
      return new Date(timeStr).getTime();
    } catch { return NaN; }
  };

  // ─── MARKER HIT EFFECT ───────────────────────────────────────────────────
  // markerType: 'start' (green) | 'finish' (orange)
  // All DOM nodes are created, animated, and self-removed — zero state leak.
  const triggerMarkerHitEffect = useCallback((chart, price, markerTimestamp, markerType = 'finish') => {
    const container = chartContainerRef.current;
    if (!container) return;

    const isStart  = markerType === 'start';
    const rgbMain  = isStart ? '0,220,80'    : '255,69,0';
    const rgbAcct  = isStart ? '0,255,120'   : '255,140,0';
    const hexMain  = isStart ? '#00dc50'     : '#ff4500';
    const hexAcct  = isStart ? '#00ff78'     : '#ff8c00';
    const hexGold  = isStart ? '#a8ff3e'     : '#ffd700';
    const hexWhite = '#ffffff';
    const flashBg  = isStart ? 'rgba(0,255,100,0.18)' : 'white';
    const flashOp  = isStart ? '1'           : '0.88';
    const bannerLabel = isStart
      ? `<div style="font-size:20px;font-weight:700;color:${hexMain};letter-spacing:3px;text-shadow:0 0 12px rgba(${rgbMain},0.7);">⏱ START</div><div style="font-size:12px;color:rgba(180,255,200,0.95);margin-top:4px;letter-spacing:1px;">베팅 시작!</div>`
      : `<div style="font-size:20px;font-weight:700;color:${hexMain};letter-spacing:3px;text-shadow:0 0 12px rgba(${rgbMain},0.6);">🏁 FINISH</div><div style="font-size:12px;color:rgba(255,200,150,0.95);margin-top:4px;letter-spacing:1px;">₿ ${(Math.round((price ?? 0) / 2) * 2).toLocaleString()}</div>`;

    // Resolve pixel x-position of the marker on the chart
    const markerPx = (() => {
      try { return chart.xAxis[0].toPixels(markerTimestamp, false); }
      catch { return container.offsetWidth * 0.62; }
    })();
    const centerY = container.offsetHeight * 0.45;

    // ── 1. Flash overlay ────────────────────────────────────────────────────
    const flash = document.createElement('div');
    Object.assign(flash.style, {
      position: 'absolute', inset: '0',
      background: flashBg, opacity: flashOp,
      zIndex: '200', borderRadius: '8px',
      pointerEvents: 'none', transition: 'opacity 0s',
    });
    container.appendChild(flash);
    requestAnimationFrame(() => {
      flash.style.transition = 'opacity 500ms ease';
      flash.style.opacity = '0';
      setTimeout(() => flash.remove(), 600);
    });

    // ── 2. Edge pulse glow ──────────────────────────────────────────────────
    const prevShadow = container.style.boxShadow;
    container.style.transition = 'box-shadow 0s';
    container.style.boxShadow = `inset 0 0 0 4px rgba(${rgbMain},0.95)`;
    setTimeout(() => {
      container.style.transition = 'box-shadow 900ms ease';
      container.style.boxShadow = prevShadow || 'none';
    }, 60);

    // ── 3. Screen shake ─────────────────────────────────────────────────────
    const prevTransform = container.style.transform;
    // START: vertical shake. FINISH: horizontal shake.
    const shakes = isStart
      ? [0, -4, 4, -3, 3, -1, 0]   // y-axis bounce for START
      : [5, -5, 4, -3, 2, -1, 0];  // x-axis rattle for FINISH
    shakes.forEach((v, i) => {
      setTimeout(() => {
        container.style.transition = 'transform 0s';
        container.style.transform  = isStart
          ? `translateY(${v}px)`
          : `translateX(${v}px)`;
        if (i === shakes.length - 1) {
          setTimeout(() => {
            container.style.transition = 'transform 200ms ease';
            container.style.transform  = prevTransform || 'none';
          }, 30);
        }
      }, i * 45);
    });

    // ── 4. Shockwave rings ──────────────────────────────────────────────────
    [0, 130, 280].forEach((delay) => {
      setTimeout(() => {
        const ring = document.createElement('div');
        Object.assign(ring.style, {
          position: 'absolute',
          width: '90px', height: '90px',
          borderRadius: '50%',
          top: `${centerY}px`, left: `${markerPx}px`,
          transform: 'translate(-50%, -50%) scale(0)',
          border: `2px solid rgba(${rgbMain},0.9)`,
          zIndex: '60', pointerEvents: 'none',
          transition: 'transform 750ms cubic-bezier(0.2,0.8,0.4,1), opacity 750ms ease-out',
        });
        container.appendChild(ring);
        requestAnimationFrame(() => {
          ring.style.transform = 'translate(-50%, -50%) scale(4.5)';
          ring.style.opacity = '0';
        });
        setTimeout(() => ring.remove(), 800);
      }, delay);
    });

    // ── 5. Vertical marker line flash ───────────────────────────────────────
    const lineFlash = document.createElement('div');
    Object.assign(lineFlash.style, {
      position: 'absolute',
      top: '0', bottom: '0',
      left: `${markerPx}px`,
      width: '3px',
      background: `rgba(${rgbMain},1)`,
      transform: 'translateX(-50%)',
      zIndex: '65', pointerEvents: 'none',
      transition: 'opacity 0s',
      boxShadow: `0 0 12px 4px rgba(${rgbMain},0.6)`,
    });
    container.appendChild(lineFlash);
    setTimeout(() => {
      lineFlash.style.transition = 'opacity 800ms ease, box-shadow 800ms ease';
      lineFlash.style.opacity = '0';
      lineFlash.style.boxShadow = 'none';
      setTimeout(() => lineFlash.remove(), 850);
    }, 80);

    // ── 6. Particle burst ───────────────────────────────────────────────────
    // const cvs = document.createElement('canvas');
    // cvs.width  = container.offsetWidth;
    // cvs.height = container.offsetHeight;
    // Object.assign(cvs.style, {
    //   position: 'absolute', inset: '0',
    //   zIndex: '70', pointerEvents: 'none',
    // });
    // container.appendChild(cvs);
    // const pCtx = cvs.getContext('2d');

    // const COLORS = isStart
    //   ? [hexMain, hexAcct, hexGold, hexWhite, '#00ffcc', '#c8ff00', '#44ff88']
    //   : ['#ff4500', '#ff8c00', '#ffd700', '#ff6347', hexWhite, '#AAFF00', '#ff3399'];

    // const pts = Array.from({ length: 100 }, () => {
    //   const ang = Math.random() * Math.PI * 2;
    //   const spd = 2.5 + Math.random() * 8;
    //   return {
    //     x: markerPx, y: centerY,
    //     vx: Math.cos(ang) * spd,
    //     vy: Math.sin(ang) * spd - 2,
    //     life: 1,
    //     decay: 0.010 + Math.random() * 0.016,
    //     r: 2 + Math.random() * 4.5,
    //     color: COLORS[Math.floor(Math.random() * COLORS.length)],
    //     rect: Math.random() > 0.45,
    //   };
    // });

    // let pRaf;
    // const drawPts = () => {
    //   pCtx.clearRect(0, 0, cvs.width, cvs.height);
    //   let alive = false;
    //   for (const p of pts) {
    //     if (p.life <= 0) continue;
    //     alive = true;
    //     pCtx.globalAlpha = Math.max(0, p.life);
    //     pCtx.fillStyle = p.color;
    //     if (p.rect) {
    //       pCtx.save();
    //       pCtx.translate(p.x, p.y);
    //       pCtx.rotate(p.life * 7);
    //       pCtx.fillRect(-p.r, -p.r / 2, p.r * 2.2, p.r);
    //       pCtx.restore();
    //     } else {
    //       pCtx.beginPath();
    //       pCtx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    //       pCtx.fill();
    //     }
    //     p.x  += p.vx;
    //     p.y  += p.vy;
    //     p.vy += 0.20;
    //     p.vx *= 0.98;
    //     p.life -= p.decay;
    //   }
    //   pCtx.globalAlpha = 1;
    //   if (alive) {
    //     pRaf = requestAnimationFrame(drawPts);
    //   } else {
    //     cancelAnimationFrame(pRaf);
    //     cvs.remove();
    //   }
    // };
    // drawPts();
    fireConfetti()

    // ── 7. Banner ────────────────────────────────────────────────────────────
    const banner = document.createElement('div');
    banner.innerHTML = bannerLabel;
    Object.assign(banner.style, {
      position: 'absolute',
      top: '26%', left: '50%',
      transform: 'translateX(-50%) scale(0)',
      background: 'rgba(0,0,0,0.93)',
      border: `1.5px solid rgba(${rgbMain},0.85)`,
      borderRadius: '10px',
      padding: '12px 30px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      zIndex: '80',
      pointerEvents: 'none',
      boxShadow: `0 0 24px rgba(${rgbMain},0.35)`,
      transition: 'transform 380ms cubic-bezier(0.34,1.56,0.64,1)',
    });
    container.appendChild(banner);
    setTimeout(() => { banner.style.transform = 'translateX(-50%) scale(1)'; }, 60);

    // Banner exit
    setTimeout(() => {
      banner.style.transition = 'transform 260ms ease-in, opacity 260ms ease-in';
      banner.style.transform  = 'translateX(-50%) scale(0)';
      banner.style.opacity    = '0';
      setTimeout(() => banner.remove(), 280);
    }, 2200);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ──────────────────────────────────────────────────────────────────────────

  const fireConfetti = () => {
  let scalar = 1.5; // Reduced for standard shapes
    confetti({
      shapes: ['circle', 'square'], // Mix of shapes
      scalar,
      particleCount: 100,
      spread: 400,
      origin: { y: 0.4 },  // Fires from the middle of the screen
      drift: 1,
      startVelocity: 50,  // High initial burst speed
      gravity: 5,        // Slightly heavier pull for a snappy "fall"
      ticks: 50
    });
  }

  // ─── Controlled series rebuild ────────────────────────────────────────────
  const rebuildSeries = useCallback((chart, trimmedData) => {
    if (!chart || !trimmedData || trimmedData.length === 0) return;
    if (isRebuildingRef.current) {
      rebuildScheduledRef.current = true;
      return;
    }

    isRebuildingRef.current     = true;
    rebuildScheduledRef.current = false;

    const savedMin      = chart.xAxis[0].min;
    const savedMax      = chart.xAxis[0].max;
    const savedPrice    = latestPriceRef.current;
    const savedTargetY  = animationRef.current.targetY;
    const savedVelocity = animationRef.current.velocity;

    try {
      chart.series[0].setData(trimmedData, false, false, false);
      dataPointsCountRef.current = trimmedData.length;

      if (Number.isFinite(savedMin) && Number.isFinite(savedMax)) {
        chart.xAxis[0].setExtremes(savedMin, savedMax, false, false);
        currentRangeRef.current = { min: savedMin, max: savedMax };
      }

      if (savedPrice !== null) {
        animationRef.current.currentY = savedPrice;
        animationRef.current.targetY  = savedTargetY ?? savedPrice;
        animationRef.current.velocity = savedVelocity ?? 0;
      }

      const lastPoint = chart.series[0].data.at(-1);
      if (lastPoint && savedPrice !== null) {
        updateLivePricePlot(chart, savedPrice);
        if (!isFillFixedRef.current) updatePriceBands(chart, savedPrice);
        updateLastPointMarker(chart, savedPrice);
      }

      chart.redraw(false);

    } catch (err) {
      console.error('[rebuildSeries] error:', err);
    } finally {
      isRebuildingRef.current = false;

      if (rebuildScheduledRef.current) {
        rebuildScheduledRef.current = false;
        setTimeout(() => {
          if (chartRef.current?.chart) cleanupOldData(chartRef.current.chart);
        }, 50);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleRebuild = useCallback((chart, trimmedData) => {
    if (rebuildTimeoutRef.current) clearTimeout(rebuildTimeoutRef.current);
    rebuildTimeoutRef.current = setTimeout(() => {
      rebuildSeries(chart, trimmedData);
    }, 100);
  }, [rebuildSeries]);


  const cleanupOldData = useCallback((chart) => {
  if (!chart || !chart.series[0] || !chart.series[0].data) return;
  if (isRebuildingRef.current) return;

  const series = chart.series[0];
  if (!series.data.length) return;

  const raw = series.data
    .filter(p => p && Number.isFinite(p.x))
    .map(p => [p.x, p.y]);

  if (!raw.length) return;

  const firstTs = raw[0][0];
  const lastTs  = raw[raw.length - 1][0];
  const spanMs  = lastTs - firstTs;

  // Only trim if the data window exceeds WINDOW_MS
  if (spanMs <= WINDOW_MS) return;

  const cutoffTs = lastTs - WINDOW_MS;
  let trimmed = raw.filter(p => p[0] >= cutoffTs);

  // Safety floor — never drop below MIN_POINTS_TO_KEEP
  if (trimmed.length < MIN_POINTS_TO_KEEP) {
    //console.log(`[cleanupOldData] Data span ${Math.round(spanMs/1000)}s exceeds window of ${Math.round(WINDOW_MS/1000)}s. Trimming data...`);
    trimmed = raw.slice(Math.max(0, raw.length - MAX_DATA_POINTS * 1.5));
  }
//console.log(`${trimmed.length} points remain after trimming. Scheduling series rebuild...`);
  scheduleRebuild(chart, trimmed);
}, [scheduleRebuild]);

  const calculateMarkerPosition = useCallback((roundData, markerType) => {
    let drawCloseTime = roundData?.draw_close_time;
    let sourceRoundId = roundData?.id;
    if (!drawCloseTime) {
      const cached = loadMarkerCache();
      if (cached) { drawCloseTime = cached.drawCloseTime; sourceRoundId = cached.roundId; }
    } else {
      saveMarkerCache(sourceRoundId, drawCloseTime);
    }
    if (!drawCloseTime) return null;
    try {
      if (markerType === 'first')  { const p = parseTimestamp(drawCloseTime); return Number.isFinite(p) ? p : null; }
      if (markerType === 'second') { const b = parseTimestamp(drawCloseTime); return Number.isFinite(b) ? b + 9000 : null; }
      return null;
    } catch { return null; }
  }, []);

  const removeAllMarkers = useCallback((chart) => {
    if (!chart || !chart.xAxis || !chart.xAxis[0]) return;
    (chart.xAxis[0].plotLinesAndBands || [])
      .filter(pl => pl.options?.id?.startsWith('marker-'))
      .map(pl => pl.options.id)
      .forEach(id => { try { chart.xAxis[0].removePlotLine(id); } catch (e) {} });
    addedMarkersRef.current.clear();
    try { chart.yAxis[0].removePlotLine('finish-price-line'); } catch (e) {}
  }, []);

  const updateMarkerColor = useCallback((chart, markerId, color, label, isHit) => {
    if (!chart || !chart.xAxis || !chart.xAxis[0]) return;
    try {
      const marker = (chart.xAxis[0].plotLinesAndBands || []).find(pl => pl.options?.id === markerId);
      if (!marker) return;
      const markerValue = marker.options.value;
      chart.xAxis[0].removePlotLine(markerId);
      let emoji = '⏱️', text = 'START';
      if (markerId.includes('second')) { emoji = isHit ? '🎯' : '🏁'; text = 'FINISH'; }
      else if (markerId.includes('first')) { emoji = isHit ? '✅' : '⏱️'; text = 'START'; }
      chart.xAxis[0].addPlotLine({
        value: markerValue, color, width: isHit ? 2 : 0.5,
        dashStyle: isHit ? 'Solid' : 'LongDash', id: markerId, zIndex: 10,
        label: {
          useHTML: true, rotation: 0,
          style: { color, fontSize: '100%', fontWeight: isHit ? 'bold' : 'normal', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', textAlign: 'center' },
          text: `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;">
                   <div style="font-size:130%;margin-bottom:170px;">${emoji}</div>
                   <div style="font-size:75%;font-weight:bold;writing-mode:vertical-rl;transform:rotate(180deg);height:40px;display:flex;align-items:center;justify-content:center;">${text}</div>
                 </div>`,
          align: 'center', y: height * 0, x: 0,
        },
      });
      safeRedraw(chart);
    } catch (e) { console.error('updateMarkerColor:', e); }
  }, [height, safeRedraw]);

  const drawFinishPriceLine = useCallback((chart, price) => {
    if (!chart || !chart.yAxis || !chart.yAxis[0] || !Number.isFinite(+price)) return;
    try { chart.yAxis[0].removePlotLine('finish-price-line'); } catch (e) {}
    const displayPrice = Math.round(price / 2) * 20;
    chart.yAxis[0].addPlotLine({
      id: 'finish-price-line', value: +price, color: 'rgba(255,69,0,0.9)',
      width: 2, dashStyle: 'ShortDash', zIndex: 50,
      label: {
        useHTML: true, align: 'right', x: 0,
        text: `<div style="background:rgba(0,0,0,0.8);color:#FF4500;padding:2px 6px;border-radius:6px;border:1px solid rgba(255,69,0,0.5);font-weight:900;font-size:90%;text-align:center;">🎯 FINISH<br/>₿: ${(displayPrice/10).toLocaleString()}</div>`,
        style: { background: 'transparent', border: 'none' },
      },
    });
  }, []);

  const checkMarkerHit = useCallback((chart, currentX, currentY) => {
    if (!chart || !currentRound) return;
    const firstMarkerTime  = calculateMarkerPosition(currentRound, 'first');
    const secondMarkerTime = calculateMarkerPosition(currentRound, 'second');
    const roundId = currentRound.id;

    if (Number.isFinite(firstMarkerTime) && !markerHitStateRef.current.firstMarker.hit && currentX >= firstMarkerTime) {
      markerHitStateRef.current.firstMarker = { hit: true, time: currentX };
      updateMarkerColor(chart, `marker-${roundId}-first`, '#00FF00', '✅ START', true);
      soundLibrary.marker2.play();

      // ── START HIT EFFECTS ────────────────────────────────────────────────
      triggerMarkerHitEffect(chart, currentY, firstMarkerTime, 'start');
    }

    if (Number.isFinite(secondMarkerTime) && !markerHitStateRef.current.secondMarker.hit && currentX >= secondMarkerTime) {
      markerHitStateRef.current.secondMarker = { hit: true, time: currentX };
      updateMarkerColor(chart, `marker-${roundId}-second`, '#FF4500', '🎯 FINISH', true);
      soundLibrary.marker1.play();

      // ── FINISH HIT EFFECTS ───────────────────────────────────────────────
      triggerMarkerHitEffect(chart, currentY, secondMarkerTime, 'finish');
    }
  }, [currentRound, calculateMarkerPosition, updateMarkerColor, triggerMarkerHitEffect]);

  const addXAxisPlotLine = useCallback((chart, valueMs, id, opts = {}, _dashStyle = 'Solid') => {
    if (!Number.isFinite(valueMs) || !chart?.xAxis?.[0]) return false;
    try { chart.xAxis[0].removePlotLine(id); } catch (e) {}
    try {
      chart.xAxis[0].addPlotLine(Object.assign({ value: valueMs, color: '#b8b9ba', width: 0.5, dashStyle: _dashStyle, id, zIndex: 10 }, opts));
      setTimeout(() => safeRedraw(chart), 100);
      return true;
    } catch { return false; }
  }, [safeRedraw]);

  const updateLivePricePlot = (chart, price) => {
    let color = '#FFAC1C', t_ico = '&#9632;';
    if (previousPriceRef.current !== null) {
      if (price > previousPriceRef.current) { color = '#00A300'; t_ico = '&#9650;'; }
      else if (price < previousPriceRef.current) { color = 'rgba(252,4,100,1)'; t_ico = '&#9660;'; }
    }
    try { chart.yAxis[0].removePlotLine('live-price-line'); } catch (e) {}
    const rp = Math.round(price / 2) * 20;
    chart.yAxis[0].addPlotLine({
      id: 'live-price-line', value: price, color: '#fff', width: 1.5, dashStyle: 'ShortDot', zIndex: 6,
      label: {
        text: `<div style="background:rgba(0,0,0,0.8);color:${color};padding:2px 6px;border-radius:6px;border:1px solid rgba(255,255,255,0.3);font-weight:900;font-size:90%;text-align:center;">LIVE&nbsp;${t_ico}<br/>₿: ${(rp/10).toLocaleString()}</div>`,
        align: 'right', x: 0, useHTML: true, style: { background: 'transparent', border: 'none' },
      },
    });
  };

  const updateLastPointMarker = (chart, price) => {
    const series = chart.series[0];
    if (!series?.data?.length) return;
    let markerColor = '#fff';
    if (previousPriceRef.current !== null) {
      if (price < previousPriceRef.current) markerColor = 'rgba(44,255,5,1)';
      else if (price > previousPriceRef.current) markerColor = 'rgba(255,49,49,1)';
    }
    const lastPoint = series.data.at(-1);
    if (!lastPoint || !Number.isFinite(lastPoint.x)) return;
    if (!lastPoint.marker || lastPoint.marker.enabled === false || lastPoint.marker.fillColor !== markerColor) {
      series.data.forEach((p) => {
        if (!p || !Number.isFinite(p.x)) return;
        if (p.marker?.enabled) p.update({ marker: { enabled: false } }, false, false);
      });
      lastPoint.update({
        marker: { enabled: true, radius: 6, fillColor: markerColor, lineColor: 'transparent', lineWidth: 2, symbol: 'diamond', states: { hover: { enabled: false } } },
      }, false, false);
    }
  };

  const easingFunctions = {
    easeOutElastic: (t) => { const p = 0.3; return Math.pow(2,-10*t)*Math.sin((t-p/4)*(2*Math.PI)/p)+1; },
    easeOutBack:    (t) => { const c1=1.70158,c3=c1+1; return 1+c3*Math.pow(t-1,3)+c1*Math.pow(t-1,2); },
    easeOutCubic:   (t) => 1-Math.pow(1-t,3),
    easeOutQuad:    (t) => 1-(1-t)*(1-t),
    easeOutQuart:   (t) => 1-Math.pow(1-t,4),
    easeInOutCubic: (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
    easeInOutQuart: (t) => t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2,
  };

  const visualPendingRef = useRef(null);

  const getZoomAnimationConfig = (zoomType) => {
    if (zoomType === 'reset') { soundLibrary.zoomOut.play(); return { duration: 1200, easing: 'easeInOutCubic' }; }
    return { duration: 1500, easing: 'easeInOutCubic', sensitivity: 0.1, interpolationFactor: 1 };
  };

  const updatePriceBands = useCallback((chart, price) => {
    try { chart.yAxis[0].removePlotBand('above-price-band'); chart.yAxis[0].removePlotBand('below-price-band'); } catch (e) {}
    const bandPrice = isFillFixed && fixedPrice !== null ? fixedPrice : price;
    const yAxis = chart.yAxis[0];
    if (isBgEnabled) {
      chart.yAxis[0].addPlotBand({ id:'above-price-band', from:bandPrice, to:yAxis.max, color:{ linearGradient:{x1:0,y1:1,x2:0,y2:0}, stops:[[0,'rgba(34,197,94,0.8)'],[0.3,'rgba(34,197,94,0.3)'],[0.6,'rgba(34,197,94,0.08)'],[1,'rgba(34,197,94,0)']] }, zIndex:1 });
      chart.yAxis[0].addPlotBand({ id:'below-price-band', from:yAxis.min, to:bandPrice, color:{ linearGradient:{x1:0,y1:0,x2:0,y2:1}, stops:[[0,'rgba(236,72,153,0.8)'],[0.3,'rgba(236,72,153,0.3)'],[0.6,'rgba(236,72,153,0.08)'],[1,'rgba(236,72,153,0)']] }, zIndex:1 });
    }
    if (isFillFixed && drawOpenPrice !== null) {
      try { chart.yAxis[0].removePlotLine('fixed-price-line'); } catch (e) {}
      chart.yAxis[0].addPlotLine({ id:'fixed-price-line', value:+drawOpenPrice, color:'rgba(38,242,0,0.7)', width:2, dashStyle:'ShortDash', zIndex:5 });
    } else {
      try { chart.yAxis[0].removePlotLine('fixed-price-line'); } catch (e) {}
    }
  }, [isFillFixed, fixedPrice]);

  const updateBandsOnExtremesChange = useCallback(() => {
    if (!chartRef.current || !initialized) return;
    if (drawOpenPrice !== null) updatePriceBands(chartRef.current.chart, drawOpenPrice);
  }, [initialized, isFillFixed, fixedPrice, updatePriceBands]);

  const centerOrIncludeMarkers = useCallback((chart, lastTs, firstMarkerTime, secondMarkerTime) => {
    const fallback = { min: lastTs - WINDOW_MS * 0.7, max: lastTs + WINDOW_MS * 0.3 };
    if (!chart?.series?.[0]?.data?.length) return fallback;
    const fp = chart.series[0].data[0];
    const lp = chart.series[0].data.at(-1);
    if (!fp?.x || !lp?.x) return fallback;

    let desiredMax = lastTs + RIGHT_PAD;
    let desiredMin = desiredMax - WINDOW_MS;

    const MARKER_PAD = 4000;
    if (Number.isFinite(firstMarkerTime) && firstMarkerTime > lastTs) {
      const n = firstMarkerTime + MARKER_PAD;
      if (n > desiredMax) { desiredMax = n; desiredMin = desiredMax - WINDOW_MS; }
    }
    if (Number.isFinite(secondMarkerTime) && secondMarkerTime > lastTs) {
      const n = secondMarkerTime + MARKER_PAD;
      if (n > desiredMax) { desiredMax = n; desiredMin = desiredMax - WINDOW_MS; }
    }

    // const clampedMin = Math.max(desiredMin, fp.x - PAST_BUFFER_MS);
    // if (desiredMax - clampedMin < WINDOW_MS * 0.5) return { min: desiredMax - WINDOW_MS, max: desiredMax };
    // return { min: clampedMin, max: desiredMax };
    return { min: desiredMin, max: desiredMax };
  }, []);

  const handleResetZoom = useCallback(() => {
    if (!chartRef.current || !initialized) return;
    const chart  = chartRef.current.chart;
    const lastTs = chart.series[0].data.at(-1)?.x || Date.now();
    const now    = Date.now();
    const fm = currentRound?.draw_close_time ? parseTimestamp(currentRound.draw_close_time) : null;
    const sm = fm ? fm + 10000 : null;
    const range = centerOrIncludeMarkers(chart, lastTs,
      Number.isFinite(fm) && fm > now ? fm : null,
      Number.isFinite(sm) && sm > now ? sm : null);
    const zc = getZoomAnimationConfig('reset');
    zoomAnimationRef.current = { startTime: performance.now(), startRange: { ...currentRangeRef.current }, targetRange: range, duration: zc.duration, easing: zc.easing };
    setIsZoomed(false);

    visualPendingRef.current = false;

    setIsAnimatingZoom(true);
  }, [initialized, currentRound, centerOrIncludeMarkers]);

  const handleZoomToPrice = useCallback(() => {
    if (!chartRef.current || !initialized || !chartRef.current.chart.series[0].data.length) return;
    const chart     = chartRef.current.chart;
    const lastPoint = chart.series[0].data.at(-1);
    const xValue    = lastPoint.x;
    const dataMin   = chart.series[0].data[0]?.x || xValue - WINDOW_MS;
    const dataMax   = chart.series[0].data.at(-1)?.x || xValue;
    const newMin    = Math.max(dataMin - PAST_BUFFER_MS, xValue - ZOOM_RANGE_MS);
    const newMax    = Math.min(dataMax + FUTURE_BUFFER_MS, xValue + ZOOM_RANGE_MS);
    const zc = getZoomAnimationConfig('zoom');
    zoomAnimationRef.current = { startTime: performance.now(), startRange: { ...currentRangeRef.current }, targetRange: { min: newMin, max: newMax }, duration: zc.duration, easing: zc.easing };
    setIsZoomed(true);

    visualPendingRef.current = true;

    setIsAnimatingZoom(true);
    if (onZoom) onZoom({ min: newMin, max: newMax });
  }, [initialized, onZoom]);

  const chartDynamicOptions = (isZoomed, chart) => {
    if (isZoomed) {
      chart.update({
        chart: {
          //backgroundColor: '#030712'
          backgroundColor: {
            radialGradient: { cx: 0.5, cy: 0.5, r: 0.5 },
            stops: [
              [0, '#581c87'], // Color in the center
              [1, '#09090b']  // Color at the edge
            ],
          },

        }
      }, false);

      chart.yAxis[0].update({ minPadding: 0.07, maxPadding: 0.08, labels: { style: { color: '#eab308', fontWeight: 'bolder' } } }, false);

      //chart.xAxis[0].update({ minPadding: 0.5, maxPadding: 0.5 }, false);

      chart.series[0].update({ 
        color: '#FDDA0D',
        lineWidth: 2.5
      });
    } else {
      chart.update({
        chart: {
          backgroundColor: '#111827'
        }
      }, false);

      chart.yAxis[0].update({ minPadding: 0.05, maxPadding: 0.05, labels: { style: { color: '#A9A9A9', fontWeight: 'normal' } } }, false);

      //chart.xAxis[0].update({ minPadding: 0.5, maxPadding: 0.5 }, false);

      chart.series[0].update({ 
        color: '#FFF',
        lineWidth: 1.2
      });
    }

   };

  // ─── Highcharts base options ──────────────────────────────────────────────
  const baseOptionsRef = useRef({
    boost: { pixelRatio:0, enabled:true, useGPUTranslations:true, usePreallocated:true, seriesThreshold:1, animationLimit:'Infinity', debug:{showSkipSummary:false,timeBufferCopy:false,timeKDTree:false,timeRendering:false,timeSeriesProcessing:false,timeSetup:false} },
    chart: {
      zoomType:'xy', type:'spline', marginRight:10, marginLeft:10, marginTop:30, marginBottom:5,
      backgroundColor:'#111827',
      plotBackgroundColor:{ linearGradient:{x1:0,y1:0,x2:0,y2:1}, stops:[[0,'rgba(45,253,177,0)'],[0.5,'rgba(45,253,177,0)'],[0.5,'rgba(253,45,173,0)'],[1,'rgba(253,45,173,0)']] },
      height, style:{fontFamily:'Inter, sans-serif',fontSize:'13px',fontWeight:'bolder'},
      animation:true, spacingTop:10, spacingBottom:50, spacingLeft:0, spacingRight:0,
      panning:{enabled:true,type:'xy'}, panKey:'shift', events:{},
    },
    title:{ text:'' },
    xAxis:{
      type:'datetime',
      labels:{ formatter:function(){ const d=new Date(this.value+9*3600000); return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')}`; }, style:{color:'#1d293d'} },
      gridLineWidth:0, lineColor:'#4a5568', tickColor:'#1d293d', plotLines:[], startOnTick:false, endOnTick:false, ordinal:false,
       minPadding: 0.2, 
      maxPadding: 0.8, 
      width: '95%',
      tickInterval: 360000
    },
    yAxis: {
      title: { text: '' },
      labels: { 
        style: { color: '#A9A9A9', fontWeight: 'bolder', fontSize: '10px', textOutline: 'none' }, 
        align: 'right', 
        x: -40, 
        autoRotation: [-45], 
        reserveSpace: true, 
        padding: 20, 
        staggerLines: 1, 
        enabled: true,
        formatter: function() {
          // Remove .00 if present
          // Show 0 decimal places for whole numbers
          return this.value.toLocaleString(undefined, {
            maximumFractionDigits: 0
          });
        }
      },
      tickPixelInterval: 50, 
      gridLineColor: '#4a5568', 
      gridLineWidth: 2, 
      gridLineDashStyle: 'ShortDash',
      startOnTick: false, 
      endOnTick: false, 
      minPadding: 0.05, 
      maxPadding: 0.05, 
      opposite: true,
      tickLength: 8, 
      tickWidth: 2, 
      tickColor: '#4a5568', 
      tickPosition: 'outside',
      overflow: 'justify',
      step: 1,
      tickPositioner: function(min, max) {
        const INTERVAL = 10, MIN_TICKS = 15, MAX_TICKS = 20, range = max - min;
        if (range < INTERVAL) { 
          const ticks = [], step = range / (Math.max(MIN_TICKS, Math.min(MAX_TICKS, 8)) - 1); 
          for (let i = 0; i < MIN_TICKS; i++) ticks.push(min + step * i); 
          return Math.ceil(ticks); 
        }
        const cnt = Math.min(MAX_TICKS, Math.max(MIN_TICKS, Math.ceil(range / INTERVAL) + 1));
        const step = range / (cnt - 1), ticks = [];
        for (let i = 0; i < cnt; i++) ticks.push(Math.round((min + step * i) / INTERVAL) * INTERVAL);
        const unique = [...new Set(ticks)].sort((a, b) => a - b);
        if (unique.length < MIN_TICKS) { 
          const fb = [], fbs = range / (MIN_TICKS - 1); 
          for (let i = 0; i < MIN_TICKS; i++) fb.push(min + fbs * i); 
          return Math.ceil(fb); 
        }
        return Math.ceil(unique);
      },
    },
    tooltip:{enabled:false}, legend:{enabled:false}, credits:{enabled:false}, exporting:{enabled:false},
    plotOptions:{ series:{ animation:{duration:300,easing:ANIMATION_TYPE}, marker:{enabled:false,states:{hover:{enabled:false}}}, turboThreshold:0, boostThreshold:1, dataGrouping:{enabled:false} } },
    series:[{ name:'Live Price', color:'#AAFF00', marker:{enabled:false}, data:[], lineWidth:1.8, animation:true }],
  });

  // Unmount cleanup
  useEffect(() => {
    return () => {
      if (chartRef.current?.chart) removeAllMarkers(chartRef.current.chart);
      if (cleanupIntervalRef.current) clearInterval(cleanupIntervalRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (socketRef.current) socketRef.current.disconnect();
      if (rebuildTimeoutRef.current) clearTimeout(rebuildTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current || !initialized) return;
    const chart = chartRef.current.chart;
    const onRedraw           = () => setTimeout(updateBandsOnExtremesChange, 10);
    const onAfterSetExtremes = () => setTimeout(updateBandsOnExtremesChange, 10);
    Highcharts.addEvent(chart, 'redraw', onRedraw);
    Highcharts.addEvent(chart.yAxis[0], 'afterSetExtremes', onAfterSetExtremes);
    const interval = setInterval(() => { if (isFillFixed) updateBandsOnExtremesChange(); }, 1000);
    return () => {
      Highcharts.removeEvent(chart, 'redraw', onRedraw);
      Highcharts.removeEvent(chart.yAxis[0], 'afterSetExtremes', onAfterSetExtremes);
      clearInterval(interval);
    };
  }, [initialized, isFillFixed, updateBandsOnExtremesChange]);

  const initChartRef = useRef(null);

  // ─── Chart initialisation ─────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;
    if (!chartRef.current) return;
    const chart = chartRef.current.chart;

    const initChart = async () => {
      try {
        setIsChartReloaded(true);
        removeAllMarkers(chart);
        setChartDataLoaded(false);
        chartDataReadyRef.current   = false;
        addedMarkersRef.current.clear();
        currentRoundIdRef.current   = null;
        animationRef.current        = { currentY:null, targetY:null, velocity:0, stiffness:0.01, damping:0.9, sensitivity:0.0000001 };
        markerHitStateRef.current   = { firstMarker:{hit:false,time:null}, secondMarker:{hit:false,time:null} };
        isRebuildingRef.current     = false;
        rebuildScheduledRef.current = false;

        const nowMs = Date.now();
        //const resp  = await fetch(`${apiUrl}/binance/historical?symbol=BTCUSDT&startTime=${nowMs-WINDOW_MS}&endTime=${nowMs}&limit=${VISIBLE_BARS}`);
         const resp  = await fetch(`${apiUrl}/binance-ws/klines-simple?symbol=BTCUSDT&startTime=${nowMs-WINDOW_MS}&endTime=${nowMs}&limit=${VISIBLE_BARS}`);
        let candles = [];
        if (resp.ok) {
          candles = (await resp.json())
            .map(c => { const t=normalizeTime(c.time??c.openTime??c.t??Date.now()); const y=Number(c.close??c.price??c.value??c.y??NaN); return Number.isFinite(y)&&Number.isFinite(t)?[t,y]:null; })
            .filter(Boolean).sort((a,b)=>a[0]-b[0]);
        }
        if (!candles.length) {
          let y = 111425.16;
          candles = Array.from({length:VISIBLE_BARS+1},(_,i)=>{ y+=Math.random()*10-5; return [Date.now()+(i-VISIBLE_BARS)*MS_PER_BAR,y]; });
        }
        if (!mounted) return;

        chart.series[0].setData(candles, true, false);
        const lastPoint = chart.series[0].data.at(-1);
        if (lastPoint) {
          animationRef.current.currentY = lastPoint.y;
          animationRef.current.targetY  = lastPoint.y;
          latestPriceRef.current = lastPoint.y;
          updateLivePricePlot(chart, lastPoint.y);
          updatePriceBands(chart, lastPoint.y);
          updateLastPointMarker(chart, lastPoint.y);
        }

        const lastTs    = lastPoint ? lastPoint.x : Date.now();
        const initRange = { min: lastTs - WINDOW_MS * 0.7, max: lastTs + WINDOW_MS * 0.3 };
        chart.xAxis[0].setExtremes(initRange.min, initRange.max, true, false);
        currentRangeRef.current    = initRange;
        dataPointsCountRef.current = chart.series[0].data.length;
        setChartDataLoaded(true);
        chartDataReadyRef.current = true;
        setInitialized(true);
      } catch (err) {
        if (!mounted) return;
        const ts    = Date.now();
        const range = { min: ts - WINDOW_MS * 0.7, max: ts + WINDOW_MS * 0.3 };
        chart.xAxis[0].setExtremes(range.min, range.max, true, false);
        currentRangeRef.current = range;
        setChartDataLoaded(true);
        chartDataReadyRef.current = true;
        setInitialized(true);
      } finally {
        setTimeout(() => setIsChartReloaded(false), 1500);
      }
    };

    initChartRef.current = initChart;
    initChart();
    return () => { mounted = false; };
  }, []);

  // ─── Marker management ────────────────────────────────────────────────────
  useEffect(() => {
    const cached = loadMarkerCache();
    if (!initialized || !chartDataLoaded || (!currentRound && !cached)) return;
    const chart = chartRef.current.chart;
    if (!chart || !chart.series[0] || !chart.series[0].data.length) return;

    const firstMarkerTime  = calculateMarkerPosition(currentRound, 'first');
    const secondMarkerTime = calculateMarkerPosition(currentRound, 'second');
    const roundId = currentRound?.id ?? cached?.roundId;
    if (!roundId) return;
    if (currentRoundIdRef.current === roundId) return;
    if (currentRoundIdRef.current && currentRoundIdRef.current !== roundId) {
      removeAllMarkers(chart);
      markerHitStateRef.current = { firstMarker:{hit:false,time:null}, secondMarker:{hit:false,time:null} };
    }
    currentRoundIdRef.current = roundId;

    const markerLabel = (emoji, text) => ({
      useHTML:true, rotation:0,
      style:{ color:'#fff', fontSize:'100%', fontWeight:'normal', textShadow:'2px 2px 4px rgba(0,0,0,0.8)', textAlign:'center' },
      text:`<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;">
              <div style="font-size:130%;margin-bottom:170px;">${emoji}</div>
              <div style="font-size:75%;font-weight:bold;writing-mode:vertical-rl;transform:rotate(180deg);height:40px;display:flex;align-items:center;justify-content:center;">${text}</div>
            </div>`,
      align:'center', y:height*0, x:0,
    });

    const addMarkersWithRetry = (attempt = 1) => {
      const markerKey = `marker-${roundId}`;
      if (addedMarkersRef.current.has(markerKey)) return;
      let added = 0;
      if (Number.isFinite(firstMarkerTime))  { if (addXAxisPlotLine(chart, firstMarkerTime,  `marker-${roundId}-first`,  { color:'#b8b9ba', width:0.5, label:markerLabel('⏱️','START') }, 'LongDash')) added++; }
      if (Number.isFinite(secondMarkerTime)) { if (addXAxisPlotLine(chart, secondMarkerTime, `marker-${roundId}-second`, { color:'#b8b9ba', width:0.5, label:markerLabel('🏁','FINISH') })) added++; }

      if (added > 0) {
        addedMarkersRef.current.add(markerKey);
        setTimeout(() => {
          if (!isZoomed && chart.series[0]?.data?.length > 0) {
            const now = Date.now(), lastTs = chart.series[0].data.at(-1).x;
            const range = centerOrIncludeMarkers(chart, lastTs,
              Number.isFinite(firstMarkerTime)  && firstMarkerTime  > now ? firstMarkerTime  : null,
              Number.isFinite(secondMarkerTime) && secondMarkerTime > now ? secondMarkerTime : null);
            chart.xAxis[0].setExtremes(range.min, range.max, true, false);
            currentRangeRef.current = range;
            setTimeout(() => safeRedraw(chart), 100);
            setTimeout(() => safeRedraw(chart), 500);
          }
        }, 500);
      } else if (attempt < 3) {
        setTimeout(() => addMarkersWithRetry(attempt + 1), 1000 * attempt);
      } else if (!Number.isFinite(secondMarkerTime) && Number.isFinite(firstMarkerTime)) {
        if (addXAxisPlotLine(chart, firstMarkerTime+10000, `marker-${roundId}-second-emergency`, { color:'#c4fa02', width:1, label:{ useHTML:true, rotation:0, style:{color:'#c4fa02',fontSize:'16px',fontWeight:'bold',textShadow:'2px 2px 4px rgba(0,0,0,0.8)'}, text:'⚑', align:'center', y:height*0.59, x:0 } })) {
          addedMarkersRef.current.add(markerKey);
        }
      }
    };

    addMarkersWithRetry(1);

    const cleanupCycleMarkers = () => {
      const data = chart.series[0]?.data;
      if (!data?.length) return;
      const lp = data.at(-1);
      if (!lp || !Number.isFinite(lp.x)) return;
      const toRemove = [];
      chart.xAxis[0].plotLinesAndBands.forEach(pl => { if (pl.options?.id?.endsWith('-second') && pl.options.value <= lp.x) toRemove.push(pl.options.id.replace('-second','')); });
      toRemove.forEach(baseId => {
        ['first','second','second-emergency'].forEach(suffix => { try { chart.xAxis[0].removePlotLine(`${baseId}-${suffix}`); } catch(e){} });
        addedMarkersRef.current.delete(baseId);
      });
    };

    cleanupIntervalRef.current = setInterval(cleanupCycleMarkers, 5000);
    return () => clearInterval(cleanupIntervalRef.current);
  }, [initialized, chartDataLoaded, currentRound, height, isZoomed, removeAllMarkers, calculateMarkerPosition, addXAxisPlotLine, centerOrIncludeMarkers, safeRedraw]);

  // ─── WebSocket ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialized) return;
    const chart  = chartRef.current.chart;
    const socket = io(`${websocketUrl}/hl_price`, { transports:['websocket'], reconnectionAttempts:5, reconnectionDelay:5000 });
    socketRef.current = socket;
    let lastTs = Date.now();

    socket.on('tradeUpdate', (data) => {
      const price = parseFloat(data?.value ?? data?.price ?? data?.p ?? data);
      if (!Number.isFinite(price) || !chart.series?.[0]?.data) return;

      const tsMs = Date.now() > lastTs ? Date.now() : lastTs + 1;
      lastTs = tsMs;
      if (animationRef.current.currentY === null) animationRef.current.currentY = price;
      animationRef.current.targetY = price;
      latestPriceRef.current = price;

      let anchor = chart.series[0].data.at(-1);
      if (!anchor || !Number.isFinite(anchor.x) || tsMs - lastRealPointTsRef.current >= MS_PER_BAR) {
        chart.series[0].addPoint([tsMs, animationRef.current.currentY ?? (anchor?.y ?? price)], false, false, false);
        lastRealPointTsRef.current = tsMs;
        dataPointsCountRef.current = chart.series[0].data.length;
      }

      const rlp = chart.series[0].data.at(-1);
      if (rlp && Number.isFinite(rlp.x) && rlp.graphic) {
        try {
          const bb = rlp.graphic.element.getBBox();
          chart.renderer.circle(bb.x+bb.width/2, bb.y+bb.height/2, 6)
            .attr({ fill:'none', stroke:'rgba(249,249,249,0.77)', 'stroke-width':PING_CONFIG.strokeWidth, 'stroke-opacity':PING_CONFIG.startOpacity, zIndex:2 })
            .add(chart.series[0].group)
            .animate({ r:PING_CONFIG.maxSize, opacity:PING_CONFIG.endOpacity }, { duration:PING_CONFIG.pingDuration, easing:PING_CONFIG.easing }, function(){ try{this.destroy();}catch(e){} });
        } catch (e) {}
      }
      previousPriceRef.current = price;
    });

    return () => socket.disconnect();
  }, [initialized]);
// Modified heartbeat effect
useEffect(() => {
  if (!initialized) return;
  
  const heartbeatInterval = setInterval(() => {
    if (latestPriceRef.current && !isFillFixed && HEARTBEAT_CONFIG.enabled) {
      const chart = chartRef.current?.chart;
      if (!chart) return;
      
      const lastPoint = chart.series[0].data.at(-1);
      if (lastPoint && animationRef.current.currentY) {
        // Slower, more gradual movement with easing
        const currentPrice = animationRef.current.currentY;
        
        // Apply sine wave instead of random walk for smoother movement
        const time = Date.now() / 1000;
        const microMove = Math.sin(time * 0.80) * HEARTBEAT_CONFIG.maxVariation;
        
        // Or use random with smaller steps
        // const microMove = (Math.random() - 0.5) * HEARTBEAT_CONFIG.maxVariation;
        
        const targetPrice = currentPrice * (1 + microMove);
        
        // Gradually adjust target instead of jumping
        const smoothedTarget = animationRef.current.targetY * (1 - HEARTBEAT_CONFIG.smoothing) + 
                              targetPrice * HEARTBEAT_CONFIG.smoothing;
        
        animationRef.current.targetY = smoothedTarget;
      }
    }
  }, HEARTBEAT_CONFIG.interval);
  
  return () => clearInterval(heartbeatInterval);
}, [initialized, isFillFixed]);

  // ─── Main rAF loop ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialized) return;
    const chart = chartRef.current.chart;

    const animate = (timestamp) => {
      frameCounterRef.current++;

      if (frameCounterRef.current % 60 === 0) {
        const now = performance.now();
        if (now - performanceMonitorRef.current.lastCleanup > 1000) {
          if (!isRebuildingRef.current && !rebuildScheduledRef.current) {
            cleanupOldData(chart);
          }
          performanceMonitorRef.current.lastCleanup = now;
        }
      }

      // Zoom interpolation
      const za = zoomAnimationRef.current;
      if (za.startRange.min !== null && Number.isFinite(za.targetRange.min) && Number.isFinite(za.targetRange.max)) {
        const t = easingFunctions[za.easing]?.(Math.min((timestamp-za.startTime)/za.duration,1)) ?? Math.min((timestamp-za.startTime)/za.duration,1);
        const iMin = za.startRange.min + (za.targetRange.min-za.startRange.min)*t;
        const iMax = za.startRange.max + (za.targetRange.max-za.startRange.max)*t;
        try { chart.xAxis[0].setExtremes(iMin, iMax, false, false); if (isZoomed && onZoom) onZoom({min:iMin,max:iMax}); } catch(e){}
        currentRangeRef.current = { min:iMin, max:iMax };
        if (t >= 1) {
          za.startRange = {min:null,max:null};
          setIsAnimatingZoom(false);
          if (visualPendingRef.current !== null) {
            chartDynamicOptions(visualPendingRef.current, chart);
            visualPendingRef.current = null;
          }
        }
      }

      if (latestPriceRef.current != null && chart.series[0].data.length > 0) {
        const lastPoint = chart.series[0].data.at(-1);
        if (!lastPoint || !Number.isFinite(lastPoint.x)) { rafRef.current = requestAnimationFrame(animate); return; }

        const anim = animationRef.current;
        if (Number.isFinite(lastPoint.y)) checkMarkerHit(chart, lastPoint.x, lastPoint.y);

        // Spring physics
        if (anim.currentY !== null && anim.targetY !== null) {
          const disp = anim.targetY - anim.currentY;
          anim.velocity = (anim.velocity + disp * anim.stiffness) * anim.damping;
          if (Math.abs(anim.velocity) > 0.0001 || Math.abs(disp) > 0.0001) {
            anim.currentY += anim.velocity;
            const mo = Math.abs(disp)*2+1;
            if (Math.abs(anim.currentY-anim.targetY) > mo) { anim.currentY = anim.targetY+Math.sign(anim.currentY-anim.targetY)*mo; anim.velocity *= 0.5; }

            if (isZoomed) {
              const now = performance.now();
              if (now - lastInterpolatedRef.current >= 20) {
                chart.series[0].addPoint([Date.now(), anim.currentY], false, false, false);
                lastInterpolatedRef.current = now;
                dataPointsCountRef.current = chart.series[0].data.length;
              }
            } else {
              lastPoint.update(anim.currentY, false, false);
            }
            updateLivePricePlot(chart, anim.currentY);
            updateLastPointMarker(chart, anim.currentY);
            if (!isFillFixed) updatePriceBands(chart, anim.currentY);
          }
        }

        // Auto-pan
        const zoomIsActive    = za.startRange.min !== null;
        const RANGE_TOLERANCE = 2000;
        if (!zoomIsActive) {
          if (isZoomed) {
            const xv   = lastPoint.x;
            const dMin = chart.series[0].data[0]?.x || xv-WINDOW_MS;
            const dMax = chart.series[0].data.at(-1)?.x || xv;
            const nMin = Math.max(dMin-PAST_BUFFER_MS, xv-ZOOM_RANGE_MS);
            const nMax = Math.min(dMax+FUTURE_BUFFER_MS, xv+ZOOM_RANGE_MS);
            if (Math.abs(currentRangeRef.current.min-nMin)>RANGE_TOLERANCE || Math.abs(currentRangeRef.current.max-nMax)>RANGE_TOLERANCE) {
              const zc = getZoomAnimationConfig('auto');
              zoomAnimationRef.current = { startTime:timestamp, startRange:{...currentRangeRef.current}, targetRange:{min:nMin,max:nMax}, duration:zc.duration, sensitivity:0.001, easing:zc.easing };
              setIsAnimatingZoom(true);
              if (onZoom) onZoom({min:nMin,max:nMax});
            }
          } else {
            const now = Date.now();
            const fm  = calculateMarkerPosition(currentRound, 'first');
            const sm  = calculateMarkerPosition(currentRound, 'second');
            const range = centerOrIncludeMarkers(chart, lastPoint.x,
              Number.isFinite(fm) && fm > now ? fm : null,
              Number.isFinite(sm) && sm > now ? sm : null);
            if (Math.abs(currentRangeRef.current.min-range.min)>RANGE_TOLERANCE || Math.abs(currentRangeRef.current.max-range.max)>RANGE_TOLERANCE) {
              const zc = getZoomAnimationConfig('auto');
              zoomAnimationRef.current = { startTime:timestamp, startRange:{...currentRangeRef.current}, targetRange:range, duration:zc.duration, easing:zc.easing };
              setIsAnimatingZoom(true);
            }
          }
        }
      }

      try { chart.redraw(false); } catch(e){}
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [initialized, isFillFixed, currentRound, updatePriceBands, isZoomed, onZoom, cleanupOldData, calculateMarkerPosition, centerOrIncludeMarkers, checkMarkerHit]);

  useEffect(() => {
    if (timerStatus !== 'betting_open' && !isFillFixed) {
      handleZoomToPrice();
      if (latestPriceRef.current !== null) {
        setIsFillFixed(true);
        setFixedPrice(latestPriceRef.current);
        if (chartRef.current) updatePriceBands(chartRef.current.chart, latestPriceRef.current);
      }
    } else if (timerStatus === 'betting_open' && isFillFixed) {
      clearMarkerCache();
      handleResetZoom();
      setIsFillFixed(false);
      setFixedPrice(null);
      if (chartRef.current && latestPriceRef.current !== null) updatePriceBands(chartRef.current.chart, latestPriceRef.current);
    }
  }, [timerStatus, currentRound, isFillFixed, updatePriceBands]);

  useEffect(() => {
    if (timerStatus === 'payout' && chartRef.current) {
      drawFinishPriceLine(chartRef.current.chart, resultsData?.draw_close_price);
    }
  }, [timerStatus]);

  // ─── Periodic refresh + visibility change ─────────────────────────────────
  useEffect(() => {
    const intervalId = setInterval(() => {
      //console.log('Periodic chart refresh');
      initChartRef.current?.();
    }, chartReloadInterval);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(() => {
          //console.log('User is back — reinitialising chart');
          initChartRef.current?.();
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <div className="z-auto">
      <div ref={chartContainerRef} className={`w-full max-w-full overflow-hidden relative ${className}`} style={{ minHeight:`${height}px` }}>
        <div className="timer absolute z-20 w-full h-[80%] overflow-hidden text-shadow-lg/30">
          {timerStatus === "betting_open" && (
            <div className="timer-cont animate-timer-open absolute z-20 p-2 text-white font-bold">
              <span className="text-3xl font-black text-emerald-500 max-sm:text-[1.5rem] mb-2">라운드 진행중</span><br />
              <span className="text-lg font-semibold max-sm:text-xs">베팅 하세요</span>
            </div>
          )}
          {timerStatus === "draw" && (
            <div className="timer-cont animate-slide-in-right-current-price absolute z-20 p-2 text-white font-bold">
              <span className="text-3xl font-black text-pink-500 max-sm:text-[1.5rem] mb-2">베팅 종료!</span><br />
              <span className="text-lg font-semibold max-sm:text-xs">다음 라운드를 기다리세요</span>
            </div>
          )}
          {timerStatus === "payout" && <WinningBetPanel />}
          <div className={"w-full change-container backdrop-blur-md " + (timerStatus === "payout" ? 'visible' : 'hidden')}>
            <div className="flex flex-row justify-center items-center p-1 max-sm:w-full sm:max-lg:w-full w-1/4 animate-slide-in-right-current-price mx-auto">
              {resultsData?.result === "up"   && <><span className="text-md font-semibold ml-2 text-emerald-500">요율 변화:</span><span className="text-md font-semibold ml-2 text-emerald-500">▲</span><span className="text-md font-bold text-emerald-500 animate-pulse">&nbsp;{resultsData.result_percentage_change}&nbsp;- {resultsData?.result.toUpperCase()}</span></>}
              {resultsData?.result === "down" && <><span className="text-md font-semibold ml-2 text-pink-500">요율 변화:</span><span className="text-md font-semibold ml-2 text-pink-500">▼</span><span className="text-md font-bold text-pink-500 animate-pulse">&nbsp;{resultsData.result_percentage_change}&nbsp; - {resultsData?.result.toUpperCase()}</span></>}
              {resultsData?.result === "draw" && <><span className="text-md font-semibold ml-2 text-amber-500">요율 변화:</span><span className="text-md font-semibold ml-2 text-amber-500">▪</span><span className="text-md font-bold text-amber-500">&nbsp;{resultsData.result_percentage_change}&nbsp; - {resultsData?.result.toUpperCase()}</span></>}
            </div>
          </div>
          {isChartReloaded && (
            <div className="text-center text-xs text-amber-400 ml-[80%] animate-fade-in">
              Loading latest data...<span className="loading loading-infinity loading-xs"></span>
            </div>
          )}
        </div>
        <div className={"p-0 bg-slate-800 " + (isChartReloaded && 'grayscale')}>
          <HighchartsReact ref={chartRef} highcharts={Highcharts} options={baseOptionsRef.current} />
        </div>
        <div className={`flex flex-col bg-slate-600/10 backdrop-blur-sm text-sm text-amber-500 text-shadow-lg/30 rounded-sm p-3 drop-shadow-2xl absolute -mt-30 ml-2 animate-slide-in-right-current-price max-sm:w-auto max-sm:text-xs ${!fixedPrice && 'hidden'}`}>
          <span className="text-xs font-medium font-sans tracking-wide text-center">시작가</span>
          <span className="divider p-0 m-0"></span>
          <span className="text-md font-extrabold animate-bounce">{(drawOpenPrice > 0) ? drawOpenPrice.toLocaleString() : 0}</span>
        </div>
        <div className="bg-slate-900 w-full">
          {timerStatus !== 'payout' && (
            <div className="animate-slide-in-right-result">
              <div className="flex flex-row pt-2 max-sm:flex-col justify-between items-center w-full bg-slate-800 text-black border border-solid border-slate-800 drop-shadow-xl/50">
                <div className="flex-1 text-left w-full border-b-1 border-slate-700"><UserBetsDetails /></div>
                <div className="flex-1 overflow-x-hidden w-full"><DrawHistory /></div>
              </div>
            </div>
          )}
          <AnimatePresence>
            {timerStatus === 'payout' && (
              <motion.div
                initial={{ y:20, opacity:0, height:'auto', width:0 }}
                animate={{ x:0, opacity:1, width:'auto' }}
                transition={{ type:"spring", stiffness:50, duration:0.5, mass:0.5, restDelta:0.5 }}
                exit={{ x:20, opacity:0, width:0, height:0 }}
              >
                <ResultSummaryPanel />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default OptionsChartHighcharts;